/* eslint-disable react/no-unknown-property */
import { Dropdown, Icon, Menu, MenuItem } from '@pypestream/design-system';
import { observer } from 'mobx-react-lite';

import {
  tableStore,
  TableRow,
  NodeColumns,
  getDefaultValueForColumn,
  isCellValueEqualToDefault,
} from '../../../stores/table-store';

export interface CellValueContextMenuProps {
  /** Row the clicked cell belongs to. */
  rowId: string;
  /** Column id of the clicked cell — the menu acts on this single cell. */
  columnId: NodeColumns;
  /** Element id the dropdown anchors to (the clicked cell's `<td>`). */
  cellId: string;
  /** Stable row index — only used for the dropdown wrapper test-id. */
  rowIndex: number;
  /** Whether the dropdown is open. */
  active: boolean;
  /** Caller maps this to its own 'closing' transition + popover/focus cleanup. */
  onRequestClose: () => void;
}

/**
 * Per-cell value context menu (Copy / Cut / Clear / Paste) scoped to a SINGLE
 * data cell.
 *
 * Restored for PSTUDIO-3164 (reviewer follow-up): right-clicking a node while a
 * CELL selection is active opens this cell-scoped menu, whereas a ROW selection
 * opens the shared {@link RowContextMenu}. Extracted out of `cell.tsx` to keep
 * that (very large) component lean and to mirror the RowContextMenu split.
 *
 * The clipboard is single-cell: each item operates on `{ columnId, rowId }` via
 * the same store actions the keyboard shortcuts use.
 *
 * EVERY item closes the menu ITSELF (`onRequestClose`), as the row menu's items
 * already do — it must not be left to the design system's `menu-item-selected`.
 * `ps-menu.handleClick` (`menu/src/menu.wc.ts:19-30`) drops a click whose item
 * is `disabled` without emitting that event, and `Clear` disables itself the
 * moment it runs: its `disabled` prop is `isCellValueEqualToDefault(...)`, which
 * clearing the cell makes true. This component is an `observer`, so React
 * re-renders the item as disabled before the click finishes bubbling — the
 * event never fires and the menu stays open over a cell it has already cleared
 * (measured on a live editor and on deployed `main`, 2026-09-18).
 *
 * Note for the history: commit `c48f919ad2` of PSTUDIO-4229 claimed to have
 * fixed that symptom through the popover stack. It fixed the CRASH and the ROW
 * menu's close; this cell-menu case has a different cause — the one above — and
 * is what actually closes it. Pinned by
 * `__tests__/cell-value-context-menu-close.test.tsx`.
 */
const CellValueContextMenuComponent = ({
  rowId,
  columnId,
  cellId,
  rowIndex,
  active,
  onRequestClose,
}: CellValueContextMenuProps) => {
  const currentCellValue = tableStore.getValue(rowId, columnId);
  const isDisabledCell = tableStore.isCellDisabled(
    rowId,
    columnId as keyof TableRow
  );

  return (
    <Dropdown
      test-id={`c-editor-table--${columnId}-td-${rowIndex}-dropdown`}
      id={`c-editor-table--${cellId}-td-dropdown`}
      key={`c-editor-table--${cellId}-td-dropdown`}
      trigger={cellId}
      placement="left-start"
      skidding={-24}
      keepMounted={false}
      disableTriggerListeners
      onclose={onRequestClose}
      active={active}
      closeOnEscClick
    >
      <Menu>
        <MenuItem
          onClick={() => {
            tableStore.handleCopy({
              cellId: columnId as keyof TableRow,
              rowId,
            });
            onRequestClose();
          }}
        >
          <Icon size="small" name="copy" slot="prefix" />
          Copy
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (isDisabledCell) return;
            tableStore.handleCut({ cellId: columnId as keyof TableRow, rowId });
            onRequestClose();
          }}
          disabled={isDisabledCell}
        >
          <Icon size="small" name="scissors" slot="prefix" />
          Cut
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (isDisabledCell) return;
            if (!tableStore.hasCell(rowId, columnId)) return;

            // Record the clear as one undo/redo command (PSTUDIO-2154).
            tableStore.recordRowEdit(
              rowId,
              `Clear ${String(columnId)}`,
              () => {
                tableStore.updateRow({
                  rowId,
                  updates: { [columnId]: getDefaultValueForColumn(columnId) },
                  skipValidationRules: false,
                  saveImmediately: true,
                });
              },
              columnId
            );
            onRequestClose();
          }}
          disabled={
            isDisabledCell ||
            isCellValueEqualToDefault(
              currentCellValue,
              getDefaultValueForColumn(columnId)
            )
          }
        >
          <Icon size="small" name="eraser" slot="prefix" />
          Clear
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (isDisabledCell) return;
            tableStore.handlePaste({ cellId: columnId, rowId });
            onRequestClose();
          }}
          disabled={isDisabledCell}
        >
          <Icon size="small" name="clipboard" slot="prefix" />
          Paste
        </MenuItem>
      </Menu>
    </Dropdown>
  );
};

export const CellValueContextMenu = observer(CellValueContextMenuComponent);
